import { FeatureKey } from '@/types/Licensing.interface';
import { searchKey } from '@/utils/textSearch';
import {
  isFeatureLocked,
  isHiddenForEmployee,
  shouldShowMenuItem
} from './menuVisibility';

/**
 * Every page the sidebar links to, flat. The menu search and the Ctrl+K
 * palette list pages from here; a test keeps it in step with the menu tree.
 */
export interface NavItem {
  path: string;
  labelKey: string;
  /** Group the page sits in, shown next to search results. */
  groupKey?: string;
  /** Role rule key for `shouldShowMenuItem`. */
  menuKey?: string;
  /** Plan feature; a locked page is not offered in search. */
  feature?: FeatureKey;
  /** Only these roles (on top of `menuKey`). */
  roles?: string[];
  /** Branch-scoped page: hidden while no branch is selected. */
  requiresBranch?: boolean;
  /** Extra words that should find the page. */
  keywords?: string[];
}

export const FOOD_DELIVERY_NAV: NavItem[] = [
  {
    path: '/integrations/food-delivery',
    labelKey: 'nav.foodDelivery.connections',
    keywords: ['yemeksepeti', 'trendyol', 'getir']
  },
  {
    path: '/integrations/food-delivery/orders',
    labelKey: 'nav.foodDelivery.orders'
  },
  {
    path: '/integrations/food-delivery/claims',
    labelKey: 'nav.foodDelivery.claims'
  },
  {
    path: '/integrations/food-delivery/reviews',
    labelKey: 'nav.foodDelivery.reviews'
  },
  {
    path: '/integrations/food-delivery/mappings',
    labelKey: 'nav.foodDelivery.mappings'
  },
  {
    path: '/integrations/food-delivery/sync',
    labelKey: 'nav.foodDelivery.sync'
  },
  {
    path: '/integrations/food-delivery/operations',
    labelKey: 'nav.foodDelivery.operations'
  },
  {
    path: '/integrations/food-delivery/monitoring',
    labelKey: 'nav.foodDelivery.monitoring'
  },
  {
    path: '/integrations/food-delivery/bulk',
    labelKey: 'nav.foodDelivery.bulk'
  }
].map((item) => ({
  ...item,
  groupKey: 'nav.foodDelivery.group',
  feature: FeatureKey.FOOD_DELIVERY
}));

const PRODUCT_GROUP = 'product.management';
const LOYALTY_GROUP = 'loyalty.management';
const LICENSING_GROUP = 'licensing.management';

export const NAV_ITEMS: NavItem[] = [
  { path: '/', labelKey: 'dashboard.main.title', keywords: ['rapor', 'satış'] },
  {
    path: '/dashboards/orders',
    labelKey: 'orders',
    feature: FeatureKey.ORDERS
  },
  {
    path: '/management/companies',
    labelKey: 'company.management',
    menuKey: 'companies'
  },
  {
    path: '/management/branches',
    labelKey: 'branch.management',
    menuKey: 'branches'
  },
  {
    path: '/management/users',
    labelKey: 'user.management',
    feature: FeatureKey.USERS,
    keywords: ['müşteri', 'personel', 'çalışan']
  },
  {
    path: '/management/categories',
    labelKey: 'category.management',
    groupKey: PRODUCT_GROUP,
    feature: FeatureKey.PRODUCTS
  },
  {
    path: '/management/products',
    labelKey: 'product.list',
    groupKey: PRODUCT_GROUP,
    feature: FeatureKey.PRODUCTS,
    keywords: ['ürün', 'fiyat']
  },
  {
    path: '/management/company-products/menus',
    labelKey: 'menu.management',
    groupKey: PRODUCT_GROUP,
    feature: FeatureKey.PRODUCTS
  },
  {
    path: '/management/stock',
    labelKey: 'stock.management',
    groupKey: PRODUCT_GROUP,
    feature: FeatureKey.STOCK
  },
  {
    path: '/management/warehouses',
    labelKey: 'warehouse.management',
    groupKey: PRODUCT_GROUP,
    feature: FeatureKey.WAREHOUSE
  },
  {
    path: '/management/recipes',
    labelKey: 'recipes.management.title',
    groupKey: PRODUCT_GROUP,
    feature: FeatureKey.RECIPE
  },
  {
    path: '/management/ingredients',
    labelKey: 'ingredients.management.title',
    groupKey: PRODUCT_GROUP,
    feature: FeatureKey.INGREDIENTS
  },
  {
    path: '/management/table-management',
    labelKey: 'table.management',
    feature: FeatureKey.TABLE_MANAGEMENT,
    keywords: ['masa', 'mutfak', 'kds']
  },
  {
    path: '/management/campaigns',
    labelKey: 'campaign.management',
    menuKey: 'campaigns',
    feature: FeatureKey.CAMPAIGNS
  },
  {
    path: '/management/packages',
    labelKey: 'package.management',
    menuKey: 'campaigns',
    feature: FeatureKey.CAMPAIGNS
  },
  {
    path: '/management/notifications',
    labelKey: 'notification.campaigns',
    groupKey: 'notification.management',
    menuKey: 'notifications',
    feature: FeatureKey.NOTIFICATIONS,
    keywords: ['bildirim', 'push']
  },
  {
    path: '/management/notifications/templates',
    labelKey: 'notification.templates',
    groupKey: 'notification.management',
    menuKey: 'notifications',
    feature: FeatureKey.NOTIFICATIONS
  },
  {
    path: '/management/memberships',
    labelKey: 'memberships',
    groupKey: 'membership.management',
    feature: FeatureKey.MEMBERSHIPS
  },
  {
    path: '/management/membership-plans',
    labelKey: 'membership.plans',
    groupKey: 'membership.management',
    feature: FeatureKey.MEMBERSHIPS
  },
  {
    path: '/management/activities',
    labelKey: 'activity.management',
    feature: FeatureKey.ACTIVITIES
  },
  {
    path: '/management/finance',
    labelKey: 'finance.title',
    menuKey: 'finance',
    feature: FeatureKey.FINANCE
  },
  {
    path: '/management/receivables',
    labelKey: 'receivables.title',
    menuKey: 'receivables',
    feature: FeatureKey.FISCAL_POS,
    requiresBranch: true
  },
  {
    path: '/management/customer-invoice-profiles',
    labelKey: 'customerInvoiceProfiles.title',
    menuKey: 'customer-invoice-profiles',
    feature: FeatureKey.FISCAL_POS
  },
  {
    path: '/management/loyalty',
    labelKey: 'loyalty.settings',
    groupKey: LOYALTY_GROUP,
    menuKey: 'loyalty-settings',
    feature: FeatureKey.MOBILE_LOYALTY
  },
  {
    path: '/management/loyalty/products',
    labelKey: 'loyalty.products',
    groupKey: LOYALTY_GROUP,
    feature: FeatureKey.MOBILE_LOYALTY
  },
  {
    path: '/management/vouchers',
    labelKey: 'voucher.templates.title',
    groupKey: LOYALTY_GROUP,
    feature: FeatureKey.MOBILE_LOYALTY,
    keywords: ['kupon']
  },
  {
    path: '/management/stamp-cards',
    labelKey: 'stampCard.title',
    groupKey: LOYALTY_GROUP,
    feature: FeatureKey.MOBILE_LOYALTY
  },
  {
    path: '/management/plans',
    labelKey: 'licensing.plans.management',
    groupKey: LICENSING_GROUP,
    menuKey: 'plans',
    roles: ['SUPER_ADMIN']
  },
  {
    path: '/management/subscriptions',
    labelKey: 'licensing.subscription.management',
    groupKey: LICENSING_GROUP,
    menuKey: 'plans',
    roles: ['SUPER_ADMIN', 'COMPANY_ADMIN']
  },
  {
    path: '/management/sms-packages',
    labelKey: 'sms.package.management',
    groupKey: LICENSING_GROUP,
    menuKey: 'plans',
    roles: ['SUPER_ADMIN']
  },
  {
    path: '/management/pos-transactions',
    labelKey: 'posTransactions.management',
    groupKey: LICENSING_GROUP,
    menuKey: 'plans',
    roles: ['SUPER_ADMIN']
  },
  {
    path: '/integrations/adisyo',
    labelKey: 'nav.adisyo',
    groupKey: 'integrations.title',
    menuKey: 'adisyo',
    feature: FeatureKey.INTEGRATIONS
  },
  ...FOOD_DELIVERY_NAV,
  {
    path: '/management/changelog',
    labelKey: 'changelog.management.title',
    roles: ['SUPER_ADMIN']
  },
  {
    path: '/downloads/desktop-app',
    labelKey: 'nav.desktopApp',
    feature: FeatureKey.DESKTOP_APP
  },
  { path: '/docs', labelKey: 'nav.docs', keywords: ['yardım', 'kılavuz'] }
];

export interface NavContext {
  role: string | null;
  tokenBranchId: number | null | undefined;
  hasFeature: (feature: FeatureKey) => boolean;
  hasBranch: boolean;
}

/** Pages the user can open right now (locked plan features excluded). */
export function visibleNavItems(
  context: NavContext,
  items: NavItem[] = NAV_ITEMS
): NavItem[] {
  const { role, tokenBranchId, hasFeature, hasBranch } = context;
  if (!role) return [];
  return items.filter(
    (item) =>
      (!item.menuKey ||
        shouldShowMenuItem(role, tokenBranchId, item.menuKey)) &&
      (!item.roles || item.roles.includes(role)) &&
      (!item.feature ||
        !isFeatureLocked(role, tokenBranchId, hasFeature, item.feature)) &&
      (!item.requiresBranch || hasBranch) &&
      !isHiddenForEmployee(role, item.path)
  );
}

/** Pages whose label, group or keywords contain every word of `query`. */
export function searchNavItems(
  items: NavItem[],
  query: string,
  translate: (key: string) => string
): NavItem[] {
  const words = searchKey(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  return items.filter((item) => {
    const haystack = searchKey(
      [
        translate(item.labelKey),
        item.groupKey ? translate(item.groupKey) : '',
        ...(item.keywords ?? [])
      ].join(' ')
    );
    return words.every((word) => haystack.includes(word));
  });
}
